
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { Application, ApplicationCompliance, ApplicationFormType, ApplicationReview, ApplicationSummary, CreateApplication, GetApplicationsFilter, SubmitApplicationCompliance, SubmitApplicationReview, UpdateApplication } from '../models/application';
import { environment } from '../../../environments/environment';
import { Guid } from 'guid-typescript';
import { BudgetPlan } from '../models/submission';


@Injectable({
  providedIn: 'root'
})
export class ApplicationService {

  private baseUrl = `${environment.apiUrl}/applications`;

  constructor(private http: HttpClient) {}

  createApplication(createApplicationDto: CreateApplication): Observable<string> {
    return this.http.post<string>(`${this.baseUrl}`, createApplicationDto);
  }

  updateApplication(updateApplicationDto: UpdateApplication): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}`, updateApplicationDto);
  }

  deleteApplication(id: Guid): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  submit(id: Guid): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${id}/submit`, null);
  }

  approve(id: Guid): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${id}/approve`, null);
  }
  
  reject(id: Guid): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${id}/reject`, null);
  }

  return(id: Guid): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${id}/return`, null);
  }

  resubmit(id: Guid): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${id}/resubmit`, null);
  }

  comply(comply: SubmitApplicationCompliance): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${comply.applicationId}/comply`, comply);
  }

  getComplies(applicationId: Guid): Observable<ApplicationCompliance[]> {
    const url = `${this.baseUrl}/${applicationId}/complies`;
    return this.http.get<ApplicationCompliance[]>(url);
  }

  review(review: SubmitApplicationReview): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${review.applicationId}/review`, review);
  }

  getReviews(applicationId: Guid): Observable<ApplicationReview[]> {
    const url = `${this.baseUrl}/${applicationId}/reviews`;
    return this.http.get<ApplicationReview[]>(url);
  }

  getApplications(filter: GetApplicationsFilter): Observable<ApplicationSummary[]> {
    let url = `${this.baseUrl}`;
    const params = filter.toQueryParams();
  
    return this.http.get<ApplicationSummary[]>(url, { params });
  }

  getApplication(id: Guid): Observable<Application> {
    return this.http.get<Application>(`${this.baseUrl}/${id}`).pipe(
      map((application) => {  
        application.budgetPlan = BudgetPlan.fromJson(application.budgetPlan); 
        return application;
     }));
  }

  downloadForms(applicationId: Guid, formTypes: ApplicationFormType[]): Observable<Blob> {
    let params = new HttpParams();
    
    formTypes.forEach(ft => {
      params = params.append('formTypes', ft.toString());
    });
  
    const url = `${this.baseUrl}/${applicationId}/forms`;
    return this.http.get(url, { params, responseType: 'blob' });
  }
}